import React from 'react';
import {
  useNotifications, FormHandlers, useModal, Action, Portal,
} from '@doveit/bricks';
import { JobApplication } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpdateAssignedUserForm, { UpdateAssignedUserFormModel } from '../update-assigned-user-form/UpdateAssignedUserForm';

export const UPDATE_ASSIGNED_AGENT_SUCCESS_MESSAGE = "L'agente incaricato è stato sostituito con successo";
export const UPDATE_ASSIGNED_AGENT_ERROR_MESSAGE = "Non è stato possibile sostituire l'agente incaricato";

interface ChildFnProps {
  openUpdateModal: VoidFunction,
  isSaving: boolean,
}

export interface UpdateAssignedUserActionProps extends React.AriaAttributes {
  label?: string,
  email?: string,
  onUpdate: (values: UpdateAssignedUserFormModel) => Promise<JobApplication>,
  children?: (props: ChildFnProps) => React.ReactNode,
  onSuccess?: (reference: JobApplication) => void,
  isRequired?: boolean,
}

function UpdateAssignedUserAction({
  email,
  label,
  children,
  onUpdate,
  onSuccess,
  isRequired = false,
  'aria-label': ariaLabel,
  ...rest
}: UpdateAssignedUserActionProps) {
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSubmit = React.useCallback(async (values: UpdateAssignedUserFormModel) => {
    try {
      setIsSaving(true);

      const updatedReference = await onUpdate(values);

      setIsSaving(false);
      modal.close();
      addSuccess(UPDATE_ASSIGNED_AGENT_SUCCESS_MESSAGE);

      onSuccess?.(updatedReference);
    } catch (err) {
      setIsSaving(false);
      addError(UPDATE_ASSIGNED_AGENT_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, modal, onSuccess, onUpdate]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  return (
    <>
      {children ? children({
        openUpdateModal: modal.open,
        isSaving,
      }) : (
        <Action
          label={label || 'Sostituisci utente'}
          aria-label={ariaLabel || "Azione per sostituire l'utente"}
          size="S"
          loading={isSaving}
          onClick={() => modal.open()}
          {...rest}
        />
      )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`Sostituisci ${(label || 'utente').toLocaleLowerCase()}`}
          aria-label="Modale per sostituire l'utente"
          footer={(
            <Action
              label="Sostituisci"
              color="primary"
              emphasis="high"
              aria-label="Azione per confermare la sostituzione dell'utente"
              onClick={submitForm}
            />
          )}
        >
          <UpdateAssignedUserForm
            label={label || 'Sostituisci utente'}
            aria-label={ariaLabel}
            initialValues={{ email }}
            isRequired={isRequired}
            onSubmit={handleSubmit}
            innerRef={formRef}
          />
        </SimpleModal>
      </Portal>
    </>
  );
}

export default UpdateAssignedUserAction;
